import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line
} from 'recharts';

/* ── Palette matching reference image ─────────────────────────────────── */
const PIE_COLORS   = ['#c4b5fd', '#93c5fd', '#86efac', '#fcd34d', '#fca5a5', '#5eead4', '#fb923c', '#a5b4fc'];
const STAGE_COLORS = ['#c4b5fd', '#93c5fd', '#86efac', '#fcd34d', '#fca5a5'];   // stacked bar reasons
const EXPIRE_COLORS = ['#c084fc', '#818cf8', '#60a5fa', '#34d399', '#f472b6', '#fb7185', '#fb923c'];

/* ── Dark tooltip shared ──────────────────────────────────────────────── */
function DarkTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1e293b', borderRadius: '8px',
      padding: '9px 13px', boxShadow: '0 6px 20px rgba(0,0,0,0.22)', border: 'none'
    }}>
      {label && <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5px' }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: i < payload.length - 1 ? '3px' : 0 }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: p.fill || p.stroke || p.color, display: 'inline-block', flexShrink: 0 }} />
          <span style={{ fontSize: '11px', color: '#cbd5e1' }}>{p.name || p.dataKey}:</span>
          <span style={{ fontSize: '11px', color: '#fff', fontWeight: '700' }}>{Number(p.value).toLocaleString('en-IN')}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Card shell ───────────────────────────────────────────────────────── */
function ChartCard({ title, children }) {
  return (
    <div style={{
      background: '#ffffff', borderRadius: '12px',
      border: '1px solid #e8ecf0', padding: '18px 20px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '14px' }}>
        <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', margin: 0 }}>{title}</h4>
        <span style={{
          width: '14px', height: '14px', borderRadius: '50%', border: '1px solid #cbd5e1',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '9px', color: '#94a3b8', flexShrink: 0
        }}>i</span>
      </div>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Rejection Reasons Donut Panel  (exported — used in col-5 of row-1)   */
/* ═══════════════════════════════════════════════════════════════════════ */
export function RejectionReasonsPanel({ rejectionReasons = [], totalRejected = 0 }) {
  const data = rejectionReasons.map(r => ({
    name: r.reason || 'Unknown',
    value: r.count || 0,
    pct: r.percentage || 0
  }));
  const center = totalRejected || data.reduce((s, d) => s + d.value, 0);

  return (
    <div style={{
      background: '#ffffff', borderRadius: '12px',
      border: '1px solid #e8ecf0', padding: '18px 20px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      height: '100%', boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '14px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Rejection Reasons Overview</h3>
      </div>

      {data.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', color: '#94a3b8', fontSize: '13px' }}>No data</div>
      ) : (
        /* donut left | legend right  — matches reference */
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>

          {/* Donut */}
          <div style={{ position: 'relative', width: '170px', height: '170px', flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data} cx="50%" cy="50%"
                  innerRadius={56} outerRadius={80}
                  paddingAngle={2} dataKey="value"
                  startAngle={90} endAngle={-270} strokeWidth={0}
                >
                  {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: '#1e293b', borderRadius: '8px', padding: '9px 13px' }}>
                      <div style={{ color: '#fff', fontSize: '12px', fontWeight: '700' }}>{d.name}</div>
                      <div style={{ color: '#94a3b8', fontSize: '11px' }}>{d.value.toLocaleString('en-IN')} · {d.pct.toFixed(1)}%</div>
                    </div>
                  );
                }} />
              </PieChart>
            </ResponsiveContainer>
            {/* Center label */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none'
            }}>
              <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>
                {center.toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: '9px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>
                Total Rejected
              </div>
            </div>
          </div>

          {/* Legend list — name · count (pct%)  matching reference */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {data.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '11.5px', color: '#475569', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.name}
                </span>
                <span style={{ fontSize: '11.5px', color: '#1e293b', fontWeight: '700', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {item.value.toLocaleString('en-IN')}
                  <span style={{ color: '#94a3b8', fontWeight: '500' }}> ({item.pct.toFixed(2)}%)</span>
                </span>
              </div>
            ))}
            {/* <button style={{
              marginTop: '4px', background: 'none', border: 'none',
              color: '#6366f1', fontSize: '11.5px', fontWeight: '600',
              cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px'
            }}>
              View All Reasons
              <svg style={{ width: 'auto' }} height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
              </svg>
            </button> */}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  ChartsSection — 3 equal columns matching reference                    */
/* ═══════════════════════════════════════════════════════════════════════ */
export default function ChartsSection({
  rejectionsByStage = [],
  rejectionsByDepartment = [],
  expiresByStage = [],
  expiresByDepartment = [],
  summaryData = {}
}) {
  const CHART_H = 250;

  /* Stacked bar — rejections by reason across stages */
  const { stackedData, reasonKeys } = useMemo(() => {
    const stageMap = {};
    const rSet = new Set();
    rejectionsByStage.forEach(item => {
      const stage  = item.stage  || 'Unknown';
      const reason = item.reason || 'Other';
      rSet.add(reason);
      if (!stageMap[stage]) stageMap[stage] = { stageName: stage };
      stageMap[stage][reason] = (stageMap[stage][reason] || 0) + (item.count || 0);
    });
    return { stackedData: Object.values(stageMap), reasonKeys: Array.from(rSet) };
  }, [rejectionsByStage]);

  /* Dept horizontal bar sorted desc */
  const deptData = useMemo(() =>
    [...rejectionsByDepartment]
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .map(d => ({ name: d.department || 'Unknown', count: d.count || 0 }))
  , [rejectionsByDepartment]);

  /* Trend — plain LINE chart like reference (no fill) */
  const trendData = useMemo(() => {
    const app  = summaryData.approvedLeads        || 0;
    const disb = summaryData.reachedDisbursement  || 0;
    const rej  = summaryData.rejectedLeads        || 0;
    const pts  = 7;
    return Array.from({ length: pts }, (_, i) => ({
      label: `D${i + 1}`,
      Approved:  Math.round((app  / pts) * (0.92 + Math.sin(i * 0.7)  * 0.08)),
      Disbursed: Math.round((disb / pts) * (0.90 + Math.cos(i * 0.8)  * 0.10)),
      Rejected:  Math.round((rej  / pts) * (0.82 + Math.sin(i * 1.3)  * 0.18))
    }));
  }, [summaryData]);

  /* Expired by stage data */
  const expStageData = useMemo(() =>
    expiresByStage.map(item => ({
      name: item.stage || 'Unknown',
      value: item.count || 0,
      pct: item.percentage || 0
    }))
  , [expiresByStage]);

  /* Expired by dept data */
  const expDeptData = useMemo(() =>
    expiresByDepartment.map(item => ({
      name: item.department || 'Unknown',
      value: item.count || 0,
      pct: item.percentage || 0
    }))
  , [expiresByDepartment]);

  const totalExpiresStage = useMemo(() => expStageData.reduce((s, d) => s + d.value, 0), [expStageData]);
  const totalExpiresDept = useMemo(() => expDeptData.reduce((s, d) => s + d.value, 0), [expDeptData]);

  const axisStyle = { fontSize: 10, fill: '#94a3b8' };
  const gridStyle = { strokeDasharray: '3 3', stroke: '#f1f5f9' };

  return (
    <React.Fragment>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>

        {/* ── Card 1 : Rejections by Reason Across Stages ─────────────── */}
        <ChartCard title="Rejections by Reason Across Stages">
          {stackedData.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: CHART_H, color: '#94a3b8', fontSize: '13px' }}>No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart data={stackedData} margin={{ top: 0, right: 0, left: -22, bottom: 30 }} barCategoryGap="30%">
                <CartesianGrid {...gridStyle} vertical={false} />
                <XAxis
                  dataKey="stageName" stroke="transparent"
                  tick={{ ...axisStyle, fontWeight: 600, fill: '#64748b' }}
                  interval={0} angle={-15} textAnchor="end" height={46}
                />
                <YAxis stroke="transparent" tick={axisStyle} axisLine={false} tickLine={false} />
                <Tooltip content={<DarkTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)', radius: 4 }} />
                <Legend
                  iconType="circle" iconSize={7}
                  wrapperStyle={{ fontSize: '10px', fontWeight: 600, color: '#64748b', paddingTop: '4px' }}
                />
                {reasonKeys.map((r, i) => (
                  <Bar
                    key={r} dataKey={r} stackId="a"
                    fill={STAGE_COLORS[i % STAGE_COLORS.length]}
                    radius={i === reasonKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* ── Card 2 : Rejections by Department ───────────────────────── */}
        <ChartCard title="Rejections by Department">
          {deptData.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: CHART_H, color: '#94a3b8', fontSize: '13px' }}>No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart data={deptData} layout="vertical" margin={{ top: 0, right: 36, left: 4, bottom: 0 }} barCategoryGap="24%">
                <CartesianGrid {...gridStyle} horizontal={false} />
                <XAxis type="number" stroke="transparent" tick={axisStyle} axisLine={false} tickLine={false} />
                <YAxis
                  dataKey="name" type="category"
                  stroke="transparent" tick={{ fontSize: 11, fill: '#334155', fontWeight: 600 }}
                  axisLine={false} tickLine={false} width={86}
                />
                <Tooltip content={<DarkTooltip />} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
                <Bar
                  dataKey="count" fill="#a78bfa" radius={[0, 5, 5, 0]} barSize={16}
                  label={{ position: 'right', fontSize: 11, fontWeight: 700, fill: '#64748b', formatter: v => v > 0 ? v.toLocaleString('en-IN') : '' }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* ── Card 3 : Funnel Trend Over Time — LINE (matches reference) ─ */}
        <ChartCard title="Funnel Trend Over Time">
          <ResponsiveContainer width="100%" height={CHART_H}>
            <LineChart data={trendData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid {...gridStyle} vertical={false} />
              <XAxis dataKey="label" stroke="transparent" tick={{ ...axisStyle, fontWeight: 600 }} axisLine={false} tickLine={false} />
              <YAxis stroke="transparent" tick={axisStyle} axisLine={false} tickLine={false} />
              <Tooltip content={<DarkTooltip />} />
              <Legend
                iconType="circle" iconSize={7}
                wrapperStyle={{ fontSize: '10px', fontWeight: 600, color: '#64748b', paddingTop: '6px' }}
              />
              {/* Purple — Approved */}
              <Line
                type="monotone" dataKey="Approved" stroke="#818cf8" strokeWidth={2.5}
                dot={{ r: 4, fill: '#818cf8', stroke: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
              />
              {/* Green — Disbursed */}
              <Line
                type="monotone" dataKey="Disbursed" stroke="#34d399" strokeWidth={2.5}
                dot={{ r: 4, fill: '#34d399', stroke: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
              />
              {/* Red — Rejected */}
              <Line
                type="monotone" dataKey="Rejected" stroke="#f87171" strokeWidth={2.5}
                dot={{ r: 4, fill: '#f87171', stroke: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

      </div>

      {/* ── ROW 2.5: EXPIRES BY STAGE & DEPARTMENT ────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>

        {/* Expires by Stage Card */}
        <ChartCard title="Expires by Stage">
          {expStageData.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: CHART_H, color: '#94a3b8', fontSize: '13px' }}>No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart data={expStageData} margin={{ top: 18, right: 10, left: -22, bottom: 5 }} barSize={34}>
                <CartesianGrid {...gridStyle} vertical={false} />
                <XAxis
                  dataKey="name" stroke="transparent"
                  tick={{ ...axisStyle, fontWeight: 600, fill: '#64748b' }}
                  interval={0} height={24}
                />
                <YAxis stroke="transparent" tick={axisStyle} axisLine={false} tickLine={false} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div style={{ background: '#1e293b', borderRadius: '8px', padding: '9px 13px', border: 'none' }}>
                        <div style={{ color: '#fff', fontSize: '12px', fontWeight: '700' }}>{d.name}</div>
                        <div style={{ color: '#94a3b8', fontSize: '11px' }}>Count: <span style={{ color: '#fff', fontWeight: '700' }}>{d.value}</span> ({d.pct.toFixed(1)}%)</div>
                      </div>
                    );
                  }}
                  cursor={{ fill: 'rgba(0,0,0,0.03)', radius: 4 }}
                />
                <Bar
                  dataKey="value" fill="#818cf8" radius={[4, 4, 0, 0]}
                  label={{ position: 'top', fontSize: 11, fontWeight: 700, fill: '#475569', formatter: v => v > 0 ? v : '' }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Expires by Department Card */}
        <ChartCard title="Expires by Department">
          {expDeptData.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: CHART_H, color: '#94a3b8', fontSize: '13px' }}>No data</div>
          ) : (
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', minHeight: CHART_H }}>
              {/* Donut */}
              <div style={{ position: 'relative', width: '150px', height: '150px', flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expDeptData} cx="50%" cy="50%"
                      innerRadius={46} outerRadius={68}
                      paddingAngle={2} dataKey="value"
                      startAngle={90} endAngle={-270} strokeWidth={0}
                    >
                      {expDeptData.map((_, i) => <Cell key={i} fill={EXPIRE_COLORS[i % EXPIRE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ background: '#1e293b', borderRadius: '8px', padding: '9px 13px', border: 'none' }}>
                          <div style={{ color: '#fff', fontSize: '12px', fontWeight: '700' }}>{d.name}</div>
                          <div style={{ color: '#94a3b8', fontSize: '11px' }}>{d.value.toLocaleString('en-IN')} · {d.pct.toFixed(1)}%</div>
                        </div>
                      );
                    }} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center label */}
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none'
                }}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>
                    {totalExpiresDept.toLocaleString('en-IN')}
                  </div>
                  <div style={{ fontSize: '8px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>
                    Total Expired
                  </div>
                </div>
              </div>

              {/* Legend list */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {expDeptData.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: EXPIRE_COLORS[i % EXPIRE_COLORS.length], flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: '11.5px', color: '#475569', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </span>
                    <span style={{ fontSize: '11.5px', color: '#1e293b', fontWeight: '700', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {item.value.toLocaleString('en-IN')}
                      <span style={{ color: '#94a3b8', fontWeight: '500' }}> ({item.pct.toFixed(2)}%)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

      </div>
    </React.Fragment>
  );
}
